import React, { useRef, useState } from 'react';
import {
	Card,
	Typography,
	Button,
	Select,
	Table,
	Descriptions,
	Spin,
	notification,
	FormInstance,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ResellerDashboard from '../ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { useGetResellerClients } from 'src/Hooks/resellerHooks';
import { CustomModal } from '@/components/CustomModal';
import { getTwilioBundleStatus, provisionNumberRequest } from '@/api/reservedNumbers';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { updateReseller } from '@/store/reseller/slice';
import posthog from 'posthog-js';
import ConfettiExplosion from 'react-confetti-explosion';
import { GetStandAloneNumberForm } from '@/pages/ReceptionistDashboard/TestItOut/Steps/GetStandAloneNumberForm';
import GetChooseNumberForm from '@/pages/ReceptionistDashboard/TestItOut/Steps/GetChooseNumberForm';
import { AvailableNumber } from '@/interfaces/IReservedNumber';
import { PhoneOutlined } from '@mui/icons-material';
import { BookOpenIcon, PhoneIcon } from 'lucide-react';
import TutorialsModal from '../TutorialsModal';
import { updateTutorialStepStatus, updateTourStatus } from '@/api/reseller';
import '../ResellerDashboard.scss';
import { bundleInfo } from 'src/enums/EInternationPhBundles';
import TourButton from '@/components/TourButton/TourButton';
import ResellerTour from '../ResellerTour/ResellerTour';
import { setTour } from '@/store/resellerTour/slice';
import { AppRoutes } from 'src/enums/ERoutes';
import { getResellerTourState } from '@/store/resellerTour/selector';

const { Title, Text } = Typography;
const { Option } = Select;

const tutorialItems = [
	{
		icon: (
			<div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
				<PhoneIcon className="text-blue-500" size={20} />
			</div>
		),
		title: 'Phone numbers',
		description: 'Configuring phone numbers for clients in My AI Frontdesk',
		url: 'https://www.myaifrontdesk.com/reseller-tutorials/configuring-phone-numbers-for-clients-in-my-ai-frontdesk',
	},
];

const ResellerPhoneNumbers: React.FC = () => {
	const resellerData = useAppSelector(getResellerData);
	const dispatch = useAppDispatch();

	const queryClient = useQueryClient();
	const formRef = useRef<FormInstance>(null);

	const [clients, setClients] = useState<any[]>([]);
	const [selectedClient, setSelectedClient] = useState<any>(null);
	const [isStandAloneNumberModalVisible, setIsStandAloneNumberModalVisible] =
		useState<boolean>(false);
	const [isBookNumberModalVisible, setIsBookNumberModalVisible] = useState<boolean>(false);
	const [ChooseNumberModalOpen, setChooseNumberModalOpen] = useState<boolean>(false);
	const [celeberate, setCeleberate] = useState(false);
	const [selectedCountry, setSelectedCountry] = useState('');
	const [isTutorialsModalOpen, setIsTutorialsModalOpen] = useState(false);

	const phoneNumbersTourRef1 = useRef<HTMLDivElement>(null);
	const phoneNumbersTourRef2 = useRef<HTMLDivElement>(null);

	const phoneNumbersTourSteps = [
		{
			title: (
				<h1 className="font-bold text-2xl">Assign phone numbers to your client's receptionists</h1>
			),
			description: (
				<p className="text-base">
					Easily assign phone numbers to your clients by selecting a subaccount and choosing a
					number—random or by area code. Instant number setup is now available for the UK and
					Australia as well.
				</p>
			),
			target: phoneNumbersTourRef1.current as HTMLElement,
		},
		{
			title: (
				<h1 className="font-bold text-2xl">
					Check all the assigned numbers and emails for all your accounts
				</h1>
			),
			description: (
				<p className="text-base">
					This table shows all of your clients' emails and reserved phone numbers for their
					usernames.
				</p>
			),
			target: phoneNumbersTourRef2.current as HTMLElement,
		},
	];

	const openStandAloneNumberModal = () => {
		posthog.capture('trying_to_get_standalone_number');
		setIsStandAloneNumberModalVisible(true);
	};

	const closeStandAloneNumberModal = () => {
		setIsStandAloneNumberModalVisible(false);
	};

	const openBookNumberModal = () => {
		setIsBookNumberModalVisible(true);
	};

	const closeBookNumberModal = () => {
		setIsBookNumberModalVisible(false);
	};

	const { mutate: provisionNumberRequestMutation, isPending: isProvisionApiPending } = useMutation({
		mutationKey: ['provisionNumberRequest'],
		mutationFn: provisionNumberRequest,
		onSuccess: (response) => {
			const data = response;
			if (data.status && data.status === 'success' && data.provisionedNumber) {
				getClientsMutation.mutate();
				setIsBookNumberModalVisible(false);
				setCeleberate(true);
				setClients((prevClients) =>
					prevClients.map((client) =>
						client.number === selectedClient.number
							? { ...client, reservedNumber: data.provisionedNumber }
							: client,
					),
				);
				setSelectedClient({ ...selectedClient, reservedNumber: data.provisionedNumber });
			}
		},
		onError: (error) => {
			console.log('error', error);

			notification.error({ message: error.message, description: 'Failed to provision number' });
		},
	});

	const handleConfirmAction = () => {
		if (formRef.current) {
			formRef.current.submit();
		}
	};

	const handleFormSubmit = (values: { country: string }) => {
		setSelectedCountry(values.country);
		closeStandAloneNumberModal();
		openBookNumberModal();
	};

	const getClientsMutation = useGetResellerClients({
		shouldFetchReservedNumbers: true,
		onSuccess: (data) => {
			setClients(data.items);
		},
	});

	const updateTutorialMutation = useMutation({
		mutationFn: updateTutorialStepStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const updateToursMutation = useMutation({
		mutationFn: updateTourStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const handleTutorialClick = () => {
		if (!resellerData?.tutorial_steps?.phone_number_tutorial) {
			updateTutorialMutation.mutate('phone_number_tutorial');
		}
		setIsTutorialsModalOpen(true);
	};

	const handleTourClick = () => {
		setIsTutorialsModalOpen(false);

		if (!resellerData?.tours?.phone_numbers) {
			updateToursMutation.mutate('phone_numbers');
		}
	};

	// Main function to book number
	const bookNumberRequest = async (localChoosen: AvailableNumber | null) => {
		if (!selectedCountry) {
			return;
		}

		const countryCode = selectedCountry.split(' - ')[2]; // Something like "US"

		let payload = {
			clientType: 'Reseller' as const,
			username: selectedClient?.number,
			countryCode,
			localChoosen: null as string | null | AvailableNumber, // Update the type to allow both string, null, and AvailableNumber
			bundleId: null as string | null,
			addressId: null as string | null,
			campaignId: selectedClient?.number as string | null,
		};
		if (localChoosen !== null) {
			payload.localChoosen = localChoosen;
		}
		if (
			selectedCountry !== '( +1 ) - United States - US' &&
			selectedCountry !== '( +1 ) - Canada - CA'
		) {
			if (countryCode === 'GB' || countryCode === 'AU') {
				payload = {
					...payload,
					bundleId: bundleInfo[countryCode as keyof typeof bundleInfo].bundleId,
					addressId: bundleInfo[countryCode as keyof typeof bundleInfo].addressId,
				};
			}
		}

		provisionNumberRequestMutation(payload);
		setIsBookNumberModalVisible(false);
		closeStandAloneNumberModal();
	};
	// //////////////////////////// --------------------////////////////////////

	const handleClientSelect = (value: string) => {
		const selected = clients.find((client) => client.number === value);
		setSelectedClient(selected);
	};

	const handleRefreshClients = () => {
		if (resellerData?.emailAddress) {
			getClientsMutation.mutate();
		}
	};

	const columns = [
		{
			title: 'Username',
			dataIndex: ['accountInfo', 'username'],
			key: 'username',
		},
		{
			title: 'Email',
			dataIndex: ['accountInfo', 'emailAddress'],
			key: 'email',
		},
		{
			title: 'Reserved Number',
			dataIndex: 'reservedNumber',
			key: 'reservedNumber',
		},
	];

	React.useEffect(() => {
		if (resellerData?.emailAddress) {
			getClientsMutation.mutate();
		}
	}, [resellerData?.emailAddress]);

	const resellerTour = useAppSelector(getResellerTourState);

	React.useEffect(() => {
		if (!resellerData?.emailAddress || resellerTour.open) {
			return;
		}

		if (!resellerData.tours?.phone_numbers) {
			updateToursMutation.mutate('phone_numbers');
			dispatch(
				setTour({
					open: true,
					currentStep: 0,
					returnTo: AppRoutes.RESELLER_PHONE_NUMBERS,
				}),
			);
		}
	}, [resellerData?.emailAddress, resellerTour.open]);

	const { data } = useQuery({
		queryKey: ['getTwilioBundleStatus', resellerData?.twilioBundleId],
		queryFn: () => getTwilioBundleStatus(resellerData?.twilioBundleId as string, true),
		enabled: !!resellerData?.twilioBundleId,
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		retry: 3,
	});

	React.useEffect(() => {
		if (data) {
			dispatch(updateReseller({ twilioBundleStatus: data.status }));
		}
	}, [data]);

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.PHONE_NUMBERS}>
			{celeberate && (
				<ConfettiExplosion
					zIndex={1000}
					force={1}
					height="220vh"
					width={3000}
					particleCount={200}
					duration={5000}
				/>
			)}
			<div className="h-full p-6">
				<div className="flex items-center justify-between mb-6">
					<div>
						<Title level={2} className="!mb-2">
							Phone Numbers Management
						</Title>
						<Text type="secondary">
							Manage your assigned phone numbers below. Imported phone numbers from your twilio will
							be available here
						</Text>
					</div>

					<div className="flex items-center gap-2">
						<Button
							type="default"
							size="large"
							onClick={handleTutorialClick}
							className={
								!resellerData.tutorial_steps?.phone_number_tutorial
									? 'bg-[#F1F8FF] hover:bg-[#F1F8FF] text-blue-600 has-ping overflow-hidden'
									: ''
							}
						>
							<BookOpenIcon size={16} />
							<span>Tutorials</span>
						</Button>
					</div>
				</div>

				<div className="max-w-5xl">
					<Card ref={phoneNumbersTourRef1} title="Assign Phone Number" className="my-3">
						<div className="grid grid-cols-3 gap-4">
							<div>
								<Text strong className="block mb-2">
									Sub-Account
								</Text>
								<div className="flex gap-2">
									{getClientsMutation.isPending ? (
										<div className="flex justify-center flex-1">
											<Spin />
										</div>
									) : (
										<>
											<Select
												showSearch
												className="flex-1"
												placeholder="Select a sub-account"
												optionFilterProp="children"
												onChange={handleClientSelect}
												value={selectedClient?.number}
												loading={getClientsMutation.isPending}
												filterOption={(input, option) =>
													(option?.children as unknown as string)
														.toLowerCase()
														.includes(input.toLowerCase())
												}
											>
												{clients.map((client) => (
													<Option key={client.number} value={client.number}>
														{client?.accountInfo?.username}
													</Option>
												))}
											</Select>
											<Button
												icon={<ReloadOutlined />}
												onClick={handleRefreshClients}
												loading={getClientsMutation.isPending}
												size="middle"
											/>
										</>
									)}
								</div>
								{selectedClient && (
									<Descriptions column={1} size="small" className="mt-4">
										<Descriptions.Item label="Email">
											{selectedClient.accountInfo.emailAddress}
										</Descriptions.Item>
										{selectedClient.reservedNumber && (
											<Descriptions.Item label="Current Number">
												{selectedClient.reservedNumber}
											</Descriptions.Item>
										)}
									</Descriptions>
								)}
							</div>
							<div>
								<Text strong className="block mb-2">
									Action
								</Text>
								{selectedClient &&
									(selectedClient.reservedNumber ? (
										<Text type="success">Number already assigned</Text>
									) : (
										<Button
											type="primary"
											onClick={openStandAloneNumberModal}
											icon={<PhoneOutlined />}
											loading={isProvisionApiPending}
										>
											Assign standalone number
										</Button>
									))}
							</div>
						</div>
					</Card>

					<Card ref={phoneNumbersTourRef2} title="Sub Accounts" className="mt-6">
						<Table
							dataSource={clients}
							columns={columns}
							rowKey="number"
							loading={getClientsMutation.isPending}
							pagination={{
								total: clients.length,
								pageSize: 10,
								showTotal: (total) => `Total ${total} items`,
							}}
						/>
					</Card>
				</div>
			</div>

			<CustomModal
				title="Get your standalone number"
				isModalOpen={isStandAloneNumberModalVisible}
				confirmAction={handleConfirmAction}
				cancelAction={closeStandAloneNumberModal}
				okButtonProps={{ loading: isProvisionApiPending }}
				okText="Get Random Number"
			>
				<GetStandAloneNumberForm
					ref={formRef}
					debrand={true}
					setCountry={(country: string) => setSelectedCountry(country)}
					onSubmit={handleFormSubmit}
					setChooseNumberModalOpen={setChooseNumberModalOpen}
				/>
			</CustomModal>
			{/* Favourite Number  Modal */}
			<CustomModal
				title="Get Your Favorite Area Code"
				isModalOpen={ChooseNumberModalOpen}
				confirmAction={() => {
					setChooseNumberModalOpen(false);
					setIsBookNumberModalVisible(false);
				}}
				cancelAction={() => setChooseNumberModalOpen(false)}
				footer={null}
			>
				<GetChooseNumberForm
					isOpen={ChooseNumberModalOpen}
					onClose={() => setChooseNumberModalOpen(false)}
					selectedCountryCode={selectedCountry.split(' - ')[2]}
					bookNumberRequest={(props) => bookNumberRequest(props ?? null)}
				/>
			</CustomModal>
			<CustomModal
				title="Save your changes"
				isModalOpen={isBookNumberModalVisible}
				confirmAction={() => bookNumberRequest(null)}
				cancelAction={closeBookNumberModal}
			>
				<Title level={4}>
					Click Ok to confirm you want to buy number for {selectedCountry.split('-')[1]}{' '}
				</Title>
			</CustomModal>
			<TutorialsModal
				isOpen={isTutorialsModalOpen}
				onClose={() => setIsTutorialsModalOpen(false)}
				tutorialItems={tutorialItems}
			>
				<TourButton onClick={handleTourClick} page="phone_numbers" />
			</TutorialsModal>
			<ResellerTour tourSteps={phoneNumbersTourSteps} />
		</ResellerDashboard>
	);
};

export default ResellerPhoneNumbers;
